import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Download, Upload, FileText, Clock } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface ContractVersioningProps {
  dealId: string;
}

export function ContractVersioning({ dealId }: ContractVersioningProps) {
  const { toast } = useToast();
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [versionNotes, setVersionNotes] = useState("");

  const { data: versions, refetch } = useQuery({
    queryKey: ["deal-documents-versions", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_documents")
        .select("*")
        .eq("deal_id", dealId)
        .eq("document_type", "contract")
        .order("contract_version", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleUploadNewVersion = async (file: File) => {
    setUploadingVersion(true);
    try {
      const latestVersion = versions?.[0]?.contract_version || 0;
      const newVersion = latestVersion + 1;

      const fileExt = file.name.split(".").pop();
      const filePath = `contracts/${dealId}/v${newVersion}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("deal-documents")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from("deal_documents")
        .insert({
          deal_id: dealId,
          document_type: "contract",
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          contract_version: newVersion,
          version_notes: versionNotes,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        });

      if (insertError) throw insertError;

      // Update previous version to point to new one
      if (versions?.[0]) {
        const { data: newDoc } = await supabase
          .from("deal_documents")
          .select("id")
          .eq("deal_id", dealId)
          .eq("contract_version", newVersion)
          .single();
        
        if (newDoc) {
          await supabase
            .from("deal_documents")
            .update({ superseded_by: newDoc.id })
            .eq("id", versions[0].id);
        }
      }

      toast({
        title: "Version uploaded",
        description: `Contract version ${newVersion} uploaded successfully`,
      });

      setVersionNotes("");
      refetch();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploadingVersion(false);
    }
  };

  const handleDownload = async (filePath: string, fileName: string) => {
    const { data, error } = await supabase.storage
      .from("deal-documents")
      .download(filePath);

    if (error) {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload New Version</CardTitle>
          <CardDescription>
            Upload a new contract version with notes about what changed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Version notes (e.g., 'Updated compensation clause, added new deliverable')"
            value={versionNotes}
            onChange={(e) => setVersionNotes(e.target.value)}
          />
          <div className="flex gap-2">
            <input
              type="file"
              id="version-upload"
              className="hidden"
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadNewVersion(file);
              }}
              disabled={uploadingVersion}
            />
            <Button
              onClick={() => document.getElementById("version-upload")?.click()}
              disabled={uploadingVersion}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploadingVersion ? "Uploading..." : "Upload New Version"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
          <CardDescription>
            {versions?.length || 0} version(s) of this contract
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {versions?.map((version, index) => (
              <div
                key={version.id}
                className="flex items-start justify-between p-4 border rounded-lg"
              >
                <div className="flex items-start gap-3 flex-1">
                  <FileText className="h-5 w-5 text-muted-foreground mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">
                        Version {version.contract_version}
                      </span>
                      {index === 0 && (
                        <Badge variant="default">Current</Badge>
                      )}
                      {version.superseded_by && (
                        <Badge variant="secondary">Superseded</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                      <Clock className="h-3 w-3" />
                      {format(new Date(version.uploaded_at), "PPp")}
                    </div>
                    {version.version_notes && (
                      <p className="text-sm bg-muted p-2 rounded mt-2">
                        {version.version_notes}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(version.file_path, version.file_name)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            ))}

            {(!versions || versions.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No contract versions uploaded yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
